import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ArrowLeftSvg } from '../../../../assets/icons';
import FoldedHandsSVG from '../../../../assets/icons/FoldedHands';
import theme from '../../../../assets/theme';
import { useDesignerUIContext } from '../../../../hooks/useDesignerUIState';
import { useImportPresentation } from '../../../../hooks/useImportPresentation';
import { useMetrics } from '../../../../hooks/useMetrics';
import { Button } from '../../../_atoms/Button';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import PresentationFileSelector from '../../../_molecules/PresentationFileSelector';
import Loader from '../../../Loader';
import { Slide } from '../../../SlideComponents/slide.types';
import { findRelatedSlidesWithoutIndex } from '../../../SlideComponents/slideUtil';
import { makeNewIdsForSlide } from '../../../SlideComponents/slideUtilCopyPaste';
import CoreAddSlidesExperience from '../CoreAddSlidesExperience';

const ImportSlides = ({
  onAddActivity,
  templateId,
  setUploadedSlidesCache,
}: {
  onAddActivity: (slides: Slide[]) => void;
  templateId: string;
  setUploadedSlidesCache: (slides: Slide[] | undefined) => void;
}) => {
  const uploadedSlidesCache = useDesignerUIContext(
    context => context.state.uploadedSlidesCache,
  );
  const metrics = useMetrics();
  const { t } = useTranslation('Host');
  const {
    upload: importPresentation,
    isUploading: isImporting,
    isSuccess,
    reset,
    isError,
    uploadedSlides,
  } = useImportPresentation();
  const [selectableSlides, setSelectableSlides] = useState<Slide[]>();
  const [fileSizeExceeded, setFileSizeExceeded] = useState(false);
  const handleFileSelected = (file: File) => {
    const fileSizeInMb = file.size * 1e-6;
    if (fileSizeInMb <= 100) {
      setFileSizeExceeded(false);
      importPresentation({ file, templateId });
      metrics.logEvent('Creator.Import.Presentation.FileSubmitted', {
        templateId,
        fileSizeInMb,
      });
    }

    if (fileSizeInMb > 100) {
      setFileSizeExceeded(true);
      metrics.logEvent('Creator.Import.Presentation.Failed.FileSizeExceeds100Mb', {
        templateId,
        fileSizeInMb,
      });
    }
  };

  const handleAddRounds = (slides: Slide[]) => {
    const newSelectedAIGeneratedRounds = makeNewIdsForSlide(
      slides.flatMap(slide => {
        const related = findRelatedSlidesWithoutIndex(slide.id, selectableSlides || []);
        return related;
      }),
    );

    onAddActivity(newSelectedAIGeneratedRounds);
  };

  useEffect(() => {
    if (uploadedSlides && uploadedSlides?.length > 0) {
      setSelectableSlides(uploadedSlides);
      setUploadedSlidesCache(uploadedSlides);
      return;
    }
    if (uploadedSlidesCache && uploadedSlidesCache?.length > 0) {
      setSelectableSlides(uploadedSlidesCache);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedSlides, uploadedSlidesCache]);

  const handleReset = () => {
    reset();
    setUploadedSlidesCache(undefined);
    setSelectableSlides(undefined);
  };

  return (
    <>
      {!(isImporting || isSuccess || isError || selectableSlides) && (
        <div style={{ paddingTop: 20, paddingLeft: 20, paddingRight: 20 }}>
          <Title $fontSize={20} style={{ marginBottom: 10 }}>
            {t('Upload slides')}
          </Title>
          {fileSizeExceeded && (
            <p style={{ fontSize: 12, textAlign: 'left' }}>
              {t(
                'fileSizeExceeded',
                'Oops looks like this presentation is too big. Please try uploading a file smaller than 100MB',
              )}
            </p>
          )}
          <div style={{ height: 150 }}>
            {templateId && (
              <PresentationFileSelector onFileSelected={handleFileSelected} />
            )}
            {!templateId && <Loader />}
          </div>
        </div>
      )}
      {(isImporting || isError) && (
        <div style={{ height: '100%', padding: '20px' }}>
          {(isImporting || isError) && (
            <div
              style={{
                display: 'flex',
                height: 30,
                alignItems: 'center',
                cursor: 'pointer',
                marginBottom: 10,
              }}
              onClick={handleReset}
            >
              <IconWrapper height="30px" width="30px" color={theme.colors.black}>
                <ArrowLeftSvg />
              </IconWrapper>
              <Title $fontSize={20}>{t('Upload slides')}</Title>
            </div>
          )}

          {isImporting && (
            <div>
              <Loader />
              <h3 style={{ width: '100%', textAlign: 'center' }}>
                {t('Importing your awesome presentation')}
              </h3>
              <p style={{ textAlign: 'center' }}>
                {t('This might take up to 30 seconds')}...
              </p>
            </div>
          )}
          {isError && (
            <div>
              <IconWrapper height="100px" width="100px">
                <FoldedHandsSVG />
              </IconWrapper>
              <h3 style={{ width: '100%', textAlign: 'center' }}>
                {t('Failed to import presentation')}
              </h3>
              <p style={{ textAlign: 'center' }}>
                {t(
                  'Make sure the presentation is correct format, not corrupted and there is no password protection',
                )}
              </p>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Button type="primary" onClick={() => reset()}>
                  {t('Please try again')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      {selectableSlides && (
        <CoreAddSlidesExperience
          rounds={selectableSlides}
          metricsContext={{ ...selectableSlides, tab: 'UploadsTab' }}
          title={'Import presentation'}
          style={{ paddingTop: 20 }}
          isDiscoverTemplate={false}
          onGoBack={handleReset}
          onAddRounds={handleAddRounds}
          isOwner={true}
        />
      )}
    </>
  );
};

const UploadsTab = ({
  onAddActivity,
  templateId,
  setUploadedSlidesCache,
}: {
  onAddActivity: (rounds: Slide[]) => void;
  templateId: string;
  setUploadedSlidesCache: (rounds: Slide[] | undefined) => void;
}) => {
  return (
    <ImportSlides
      setUploadedSlidesCache={setUploadedSlidesCache}
      onAddActivity={onAddActivity}
      templateId={templateId}
    />
  );
};

export default UploadsTab;

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.greyVeryDark};
  display: flex;
  align-items: center;
  margin-bottom: 0px;

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 5}px;
  }
`;
